import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import it from "@/i18n/it";
import en from "@/i18n/en";

const DICTS = { it, en };
const STORAGE_KEY = "nvl.language";

const LanguageContext = createContext(null);

function resolveDefault() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "it" || stored === "en") return stored;
  } catch {}
  return "it";
}

function getPath(obj, path) {
  return path.split(".").reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(resolveDefault);

  const setLang = useCallback((next) => {
    if (next !== "it" && next !== "en") return;
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {}
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback(
    (path, paramsOrFallback, maybeFallback) => {
      const value = getPath(DICTS[lang], path);
      let params, fallback;
      if (paramsOrFallback && typeof paramsOrFallback === "object") {
        params = paramsOrFallback;
        fallback = maybeFallback;
      } else {
        fallback = paramsOrFallback;
      }
      let out = value === undefined ? (fallback ?? path) : value;
      if (params && typeof out === "string") {
        out = out.replace(/\{(\w+)\}/g, (m, k) => (params[k] !== undefined ? String(params[k]) : m));
      }
      return out;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useI18n must be used within LanguageProvider");
  return ctx;
}
